/**
 * RAG Vector (Pinecone) Configuration Page
 * Tenant-owned Pinecone credentials — no global fallback. If unconfigured or
 * inactive, RAG is unavailable for this tenant everywhere (ingestion + query).
 */
import StatusChip from '@/components/ui/StatusChip';
import PageHeader from '@/components/ui/PageHeader';
import { useRagVectorConfig } from '@/hooks/useRagVectorConfig';
import { Check, Error as ErrorIcon, Save, Science, Sync } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { usePlanGate } from '@/hooks/usePlanGate';

const RagVectorConfig: React.FC = () => {
  const { guard } = usePlanGate();
  const {
    config,
    loading,
    testing,
    backfilling,
    error,
    success,
    saveConfig,
    testConnection,
    backfillAgents,
    refetch,
    updateRagEnabled,
    updateRagChannel,
  } = useRagVectorConfig();

  const [apiKey, setApiKey] = useState('');
  const [indexName, setIndexName] = useState('');
  const [environment, setEnvironment] = useState('');
  const [testResult, setTestResult] = useState<{ valid: boolean; error?: string } | null>(null);
  const [ragEnabled, setRagEnabled] = useState(true);
  const [togglingRag, setTogglingRag] = useState(false);

  useEffect(() => {
    setIndexName(config.indexName);
    setEnvironment(config.environment);
    setRagEnabled(config.ragEnabled ?? true);
  }, [config.indexName, config.environment, config.ragEnabled]);

  // A blank apiKey is only valid when there's an existing saved key to fall
  // back to (changing just the index name/environment) — see resolveApiKey
  // on the backend. With nothing configured yet, a key is mandatory.
  const canSubmitWithoutKey = config.configured;
  const missingRequiredKey = !apiKey && !canSubmitWithoutKey;

  const handleRagToggle = async (checked: boolean) => {
    const previous = ragEnabled;
    setRagEnabled(checked); // optimistic update
    setTogglingRag(true);
    try {
      await updateRagEnabled(checked);
    } catch {
      setRagEnabled(previous); // revert on error
    } finally {
      setTogglingRag(false);
    }
  };

  const handleTest = async () => {
    setTestResult(null);
    const result = await testConnection(apiKey || undefined, indexName);
    setTestResult(result);
  };

  const handleSave = async () => {
    await saveConfig({ apiKey: apiKey || undefined, indexName, environment: environment || undefined });
    setApiKey('');
    setTestResult(null);
    refetch();
  };

  const statusChip = () => {
    if (!config.configured) {
      return <StatusChip status="inactive" label="Not configured" dot />;
    }
    if (config.lastValidationStatus === 'valid') {
      return <StatusChip status="active" label="Connected" dot />;
    }
    return <StatusChip status="warning" label="Needs attention" dot />;
  };

  if (loading && !config.configured && !apiKey) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="RAG Vector Database"
        subtitle="Connect your own Pinecone account — each agent gets its own isolated namespace inside your index"
      />

      {success && (
        <Alert severity="success" icon={<Check />} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Typography variant="h6">Pinecone Credentials</Typography>
                {statusChip()}
              </Box>

<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Retrieval uses only your own Pinecone index. Until it is configured and validated,
                answers fall back to the assistant&apos;s general response.
              </Typography>

              <TextField
                fullWidth
                type="password"
                label="Pinecone API Key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={config.configured ? '••••••••••••••••' : 'pcsk_...'}
                helperText={
                  config.configured
                    ? 'Leave blank to keep the currently saved key — enter a new one to replace it.'
                    : 'From your Pinecone project → API Keys.'
                }
                sx={{ mb: 2 }}
              />

              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Index Name"
                    value={indexName}
                    onChange={(e) => setIndexName(e.target.value)}
                    placeholder="my-tenant-index"
                    helperText="The index you created in your own Pinecone account."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Environment (optional)"
                    value={environment}
                    onChange={(e) => setEnvironment(e.target.value)}
                    placeholder="us-east-1-aws"
                    helperText="Only needed for pod-based (legacy) Pinecone indexes."
                  />
                </Grid>
              </Grid>

              {testResult && (
                <Alert
                  severity={testResult.valid ? 'success' : 'error'}
                  icon={testResult.valid ? <Check /> : <ErrorIcon />}
                  sx={{ mb: 2 }}
                >
                  {testResult.valid
                    ? 'Connection successful — credentials and index are valid.'
                    : testResult.error || 'Could not connect with these credentials.'}
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  startIcon={testing ? <CircularProgress size={16} /> : <Science />}
                  onClick={guard('enableRag', handleTest)}
                  disabled={testing || !indexName || missingRequiredKey}
                >
                  {testing ? 'Testing…' : 'Test Connection'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={guard('enableRag', handleSave)}
                  disabled={loading || !indexName || missingRequiredKey}
                >
                  {loading ? 'Saving…' : 'Save Configuration'}
                </Button>
              </Box>

              {config.lastValidationStatus === 'invalid' && config.lastValidationError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 2 }}>
                  Last validation error: {config.lastValidationError}
                </Typography>
              )}

              {config.configured && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2">Resync all agents</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Re-pushes every agent's knowledge base. Also runs when you save credentials.
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={backfilling ? <CircularProgress size={16} /> : <Sync />}
                      onClick={guard('enableRag', () => backfillAgents())}
                      disabled={backfilling}
                      sx={{ flexShrink: 0 }}
                    >
                      {backfilling ? 'Resyncing…' : 'Resync All Agents'}
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="h6">RAG by Channel</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Turn a channel off to skip knowledge-base lookups and reply directly through OpenRouter.
                  </Typography>
                </Box>
              </Box>

              {/* One independent switch per channel. WhatsApp uses `ragEnabled`
                  (settings.rag.enabled); the other three use settings.rag.channels.*.
                  All default ON (absent = on). */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                {([
                  {
                    label: 'WhatsApp',
                    checked: ragEnabled,
                    onToggle: (checked: boolean) => guard('enableRag', handleRagToggle)(checked),
                  },
                  {
                    label: 'Instagram',
                    checked: config.instagramRagEnabled,
                    onToggle: (checked: boolean) =>
                      guard('enableRag', () => updateRagChannel('instagramRagEnabled', checked))(),
                  },
                  {
                    label: 'Playground',
                    checked: config.playgroundRagEnabled,
                    onToggle: (checked: boolean) =>
                      guard('enableRag', () => updateRagChannel('playgroundRagEnabled', checked))(),
                  },
                  {
                    label: 'Website widget',
                    checked: config.widgetRagEnabled,
                    onToggle: (checked: boolean) =>
                      guard('enableRag', () => updateRagChannel('widgetRagEnabled', checked))(),
                  },
                ] as const).map((row) => (
                  <Box
                    key={row.label}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
                  >
                    <Box>
                      <Typography variant="body1">{row.label}</Typography>
                      {!row.checked && (
                        <Typography variant="caption" color="warning.main">
                          Off — replies skip knowledge-base lookups.
                        </Typography>
                      )}
                    </Box>
                    <Switch
                      checked={row.checked}
                      onChange={(_, checked) => row.onToggle(checked)}
                      disabled={togglingRag}
                      color="primary"
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RagVectorConfig;
